import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { type DlnNumberSuggestionResponse } from './types';
import { zodDlnNumberSuggestionResponseSchema } from './validations';

/**
 * Fetches a suggested delivery-note number generated from the current UTC
 * timestamp (YYYYMMDDHHmmssSSS). Guaranteed unique within the caller's
 * accessible DLNs; on collision the generator advances by 1 ms.
 *
 * @param options - Optional request options (e.g. abort signal).
 * @returns The suggested DLN number.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_dln_number_suggestion_delivery_note_suggestions_dln_number_get
 */
export const fetchDlnNumberSuggestion = async (
  options?: KyRequestOptions,
): Promise<DlnNumberSuggestionResponse> => {
  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_DLN_NUMBER_SUGGESTION(), options)
    .json<DlnNumberSuggestionResponse>();

  const validated = zodDlnNumberSuggestionResponseSchema.safeParse(response);

  if (validated.error?.issues?.length) {
    log.error({
      message: 'DLN number suggestion API response has validation errors',
      error: {
        errors: validated.error.issues,
        errorMessage: validated.error.message,
        response,
      },
    });
  }

  return response;
};
